import {ClientProperties, ScenarioRecording, ScenarioRecordingState, UserRecordingChecklist, WebClientType} from "@models";
import {ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, effect, inject, input, output, signal} from "@angular/core";
import {EventEmitterService} from "@event-emitter";
import {SimChecklistService, SimDeviceMappingService, SimUtilityService} from "@services";
import {DetachableChangeDetection} from "../panel-components/detachable-cd/detachable-change-detection";
import {Translations} from "@translations";
import {AssessablePanelMode} from "@common/components/assessment/utils";

@Component({
	selector: "sim-checklist-pane",
	templateUrl: "./checklist-pane.component.html",
	styleUrls: ["./checklist-pane.component.scss"],
	providers: [EventEmitterService],
	changeDetection: ChangeDetectionStrategy.Default,
	standalone: false,
})
export class ChecklistPaneComponent implements DetachableChangeDetection {
	protected readonly T = Translations;
	protected readonly AssessablePanelMode = AssessablePanelMode;
	protected readonly ScenarioRecordingState = ScenarioRecordingState;

	// Injections
	private readonly simChecklistService = inject(SimChecklistService);
	private readonly simEventEmitterService = inject(EventEmitterService);
	private readonly changeDetector = inject(ChangeDetectorRef);

	// I/O
	public recording = input<ScenarioRecording | null>(null);
	public userToLoadChecklistsFor = input<string | null>(null);
	public isInUndockedState = input.required<boolean>();

	public jumpToChecklistItem = output<number>();

	// Signals
	private recordingId = computed(() => this.recording()?.id);
	private clientType = computed(() => this.computeClientType(SimDeviceMappingService.clientProperties()));

	// serves as a signal to reload the checklists
	private bump = signal<number>(0);

	protected checklists = signal<UserRecordingChecklist[] | null>(null);
	protected isLoading = signal<boolean>(true);

	// Properties
	protected selectedTabIndex = 0;

	// Effects
	private effectLoadChecklists = effect(async () => {
		const recordingId = this.recordingId();
		const userToLoadChecklistsFor = this.userToLoadChecklistsFor();
		const clientType = this.clientType();
		const _ = this.bump();
		if (!recordingId) {
			return;
		}
		this.isLoading.set(true);
		const checklists = await SimUtilityService.promiseTakeAtLeast(
			350,
			this.loadChecklists(recordingId, userToLoadChecklistsFor, clientType),
		);
		const sortedChecklists = [...checklists].sort((a, b) => a.name.localeCompare(b.name));

		this.checklists.set(sortedChecklists);
		this.isLoading.set(false);
	});

	private effectReloadOnDock = effect(() => {
		const inUndockedState = this.isInUndockedState();
		if (!inUndockedState) {
			this.bump.update((x) => x + 1);
		}
	});

	constructor() {
		this.simEventEmitterService.onUserRecordingChecklistChanged.subscribe(({userRecordingChecklist}) => {
			const checklists = this.checklists();
			if (!checklists) {
				this.bump.update((x) => x + 1);
				return;
			}

			if (userRecordingChecklist) {
				const checklistToUpdate = checklists.find((c) => c.id === userRecordingChecklist.id);
				if (checklistToUpdate) {
					Object.assign(checklistToUpdate, userRecordingChecklist);
					this.checklists.set([...checklists]);
				}
			}
		});
	}

	public attachToCD() {
		this.changeDetector.reattach();
	}

	public detachFromCD() {
		this.changeDetector.detach();
	}

	private async loadChecklists(
		recordingId: string,
		userToLoadChecklistsFor: string | null,
		clientType: WebClientType | undefined,
	): Promise<UserRecordingChecklist[]> {
		if (userToLoadChecklistsFor) {
			return await this.simChecklistService.getUserChecklistsFromScenarioRecording(
				recordingId,
				userToLoadChecklistsFor,
				// @ts-ignore - TODO: this needs to be fixed in the generator
				clientType,
			);
		} else {
			return await this.simChecklistService.getUserChecklistsFromScenarioRecording(
				recordingId,
				// @ts-ignore - TODO: this needs to be fixed in the generator
				null,
				// @ts-ignore - TODO: this needs to be fixed in the generator
				clientType,
			);
		}
	}

	private computeClientType(clientProperties: ClientProperties) {
		if (clientProperties.isRecorder) {
			return WebClientType.Recorder;
		} else if (clientProperties.isTablet) {
			return WebClientType.Tablet;
		} else if (clientProperties.isViewingStation) {
			return WebClientType.ViewingStation;
		}
		return undefined;
	}
}
